import type { FocusEvent, ReactNode, WheelEvent } from 'react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';

import { cn } from '@/lib/cn';

type Props = {
  /** input 要素の `type` 値 */
  type?: HTMLInputElement['type'];
  /** フォームにエラーがあるかどうか */
  error?: boolean;
  /** コンポーネントの幅 */
  width?: number | string;
  /** オートフォーカスを行うかどうか */
  autoFocus?: boolean;
  /** コンポーネント内の先頭に表示する要素 */
  prefix?: ReactNode;
  /** コンポーネント内の末尾に表示する要素 */
  suffix?: ReactNode;
  /** コンポーネント内に表示するプレースホルダー */
  placeholder?: string;
  /** 複数行入力の際にテキストエリアの行数を指定 */
  rows?: number;
  /** prefix のクリックイベントを無視するかどうか */
  ignorePrefixClick?: boolean;
  /** テキストエリアの高さをコンテンツに合わせて自動調整するかどうか */
  autoHeight?: boolean;
  /**
   * ボーダーを下辺のみにするかどうか
   * true の場合: border 下のみ・角丸なし
   */
  borderBottom?: boolean;
};

type ElementProps = Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  keyof Props
>;

const InputTextArea = forwardRef<HTMLTextAreaElement, Props & ElementProps>(
  (
    {
      type,
      onFocus,
      onBlur,
      autoFocus,
      prefix,
      suffix,
      className,
      width = 180,
      disabled,
      error,
      placeholder,
      rows = 3,
      ignorePrefixClick = false,
      autoHeight = false,
      borderBottom = false,
      ...restProps
    },
    ref,
  ) => {
    const innerRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
      ref,
      () => innerRef.current,
    );

    const handleFocus = (e: FocusEvent<HTMLTextAreaElement>) => {
      onFocus?.(e);
    };

    const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
      onBlur?.(e);
    };

    const handleWheel = (e: WheelEvent<HTMLTextAreaElement>) => {
      if (type === 'number') {
        (e.target as HTMLTextAreaElement).blur();
      }
    };

    useEffect(() => {
      if (autoFocus && innerRef.current) {
        innerRef.current.focus();
      }
    }, [autoFocus]);

    // コンテンツに合わせてテキストエリアの高さを自動調整
    useLayoutEffect(() => {
      if (!autoHeight || !innerRef.current) return;
      const el = innerRef.current;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    });

    const wrapperClasses = cn(
      'flex items-center',
      borderBottom ? 'border-0 border-b rounded-none' : 'border rounded-md',
      disabled
        ? 'pointer-events-none border-neutral-border-disabled'
        : 'border-neutral-muted-border',
      error ? 'border-red-500' : 'border-neutral-muted-border',
      className,
    );

    const inputClasses = cn(
      'w-full p-3 overflow-hidden bg-transparent py-2 text-sm leading-normal',
      borderBottom ? 'rounded-none focus-visible:rounded-sm' : 'rounded-md',
      'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-neutral-muted-border',
      disabled
        ? borderBottom
          ? 'text-neutral-on-fill-disabled'
          : 'bg-neutral-fill-disabled opacity-100'
        : 'text-black',
      autoHeight && 'overflow-hidden resize-none',
    );

    return (
      <span
        className={wrapperClasses}
        style={{ width: typeof width === 'number' ? `${width}px` : width }}
        onClick={() => innerRef.current?.focus()}
        onKeyUp={() => innerRef.current?.focus()}
        role="presentation"
      >
        {prefix && (
          <span
            className="ml-2 mr-2 shrink-0"
            role="presentation"
            onClick={ignorePrefixClick ? (e) => e.stopPropagation() : undefined}
            onKeyUp={ignorePrefixClick ? (e) => e.stopPropagation() : undefined}
            onKeyDown={
              ignorePrefixClick ? (e) => e.stopPropagation() : undefined
            }
          >
            {prefix}
          </span>
        )}
        <textarea
          {...restProps}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onWheel={handleWheel}
          disabled={disabled}
          ref={innerRef as React.Ref<HTMLTextAreaElement>}
          aria-invalid={error || undefined}
          placeholder={placeholder}
          className={inputClasses}
          rows={autoHeight ? 1 : rows}
        />
        {suffix && <span className="ml-2 shrink-0">{suffix}</span>}
      </span>
    );
  },
);

InputTextArea.displayName = 'Input';

export default InputTextArea;
